import React from 'react'
import { Box, Skeleton } from '@mui/material'
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import CloseIcon from '@mui/icons-material/Close'
import { AGE, C, TONE } from './tokens'
import { span } from './model'

const ICON_TONE = {
  neutral: { bg: C.mutedBg, fg: C.muted },
  warn: { bg: AGE.warn.bg, fg: AGE.warn.ink },
  late: { bg: AGE.late.bg, fg: AGE.late.ink },
  transit: { bg: TONE.transit.bg, fg: TONE.transit.fg },
  good: { bg: TONE.delivered.bg, fg: TONE.delivered.fg },
}

function Sparkline({ points }) {
  if (!points || points.length < 3) return null
  const w = 84
  const h = 28
  const pad = 3
  const vals = points.map(p => p.minutes)
  const lo = Math.min(...vals) - 2
  const hi = Math.max(...vals) + 2
  const x = i => pad + (i / (points.length - 1)) * (w - pad * 2)
  const y = v => pad + (1 - (v - lo) / (hi - lo)) * (h - pad * 2)
  const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(p.minutes).toFixed(1)}`).join(' ')
  const area = `${line} L${x(points.length - 1).toFixed(1)},${h} L${x(0).toFixed(1)},${h} Z`
  const last = points[points.length - 1]
  return (
    <svg
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      style={{ flexShrink: 0, overflow: 'visible' }}
      role="img"
      aria-label={`Last ${points.length} deliveries took ${Math.round(Math.min(...vals))} to ${Math.round(Math.max(...vals))} minutes`}
    >
      <path d={area} fill="rgba(16,24,40,0.06)" />
      <path d={line} fill="none" stroke="rgba(93,102,121,0.7)" strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={x(points.length - 1)} cy={y(last.minutes)} r={2.75} fill={C.text} stroke={C.card} strokeWidth={1.5} />
      {points.map((p, i) => (
        <circle key={p.id} cx={x(i)} cy={y(p.minutes)} r={6} fill="transparent">
          <title>{`#${p.orderNo} · ${Math.round(p.minutes)} min`}</title>
        </circle>
      ))}
    </svg>
  )
}

function KpiCard({ id, active, onClick, loading, icon, tone, label, hint, value, caption, captionColor, aside }) {
  const on = active === id
  const t = ICON_TONE[tone]
  return (
    <Box
      component="button"
      type="button"
      aria-pressed={on}
      title={hint || `Show only: ${label.toLowerCase()}`}
      onClick={() => onClick(id)}
      sx={{
        position: 'relative', display: 'flex', flexDirection: 'column', gap: 1, minWidth: { xs: 196, lg: 0 }, flexShrink: 0,
        p: 1.75, textAlign: 'left', font: 'inherit', cursor: 'pointer', scrollSnapAlign: 'start',
        bgcolor: C.card, borderRadius: '12px', border: `1px solid ${on ? C.ink : C.border}`,
        boxShadow: on ? `0 0 0 1px ${C.ink}` : 'none',
        transition: 'border-color 120ms, box-shadow 120ms',
        '&:hover': { borderColor: on ? C.ink : C.input },
        '&:focus-visible': { outline: 'none', boxShadow: `0 0 0 3px rgba(141,151,168,0.35)` },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
        <Box sx={{ width: 24, height: 24, borderRadius: '6px', bgcolor: t.bg, color: t.fg, display: 'flex', alignItems: 'center', justifyContent: 'center', '& svg': { fontSize: 15 } }}>
          {icon}
        </Box>
        <Box component="span" sx={{ fontSize: 13, fontWeight: 500, color: C.muted, whiteSpace: 'nowrap' }}>{label}</Box>
        {on && (
          <Box component="span" sx={{ ml: 'auto', display: 'inline-flex', alignItems: 'center', gap: 0.25, px: 0.75, py: 0.25, borderRadius: 999, bgcolor: C.ink, color: '#fff', fontSize: 10, fontWeight: 500 }}>
            Filtering <CloseIcon sx={{ fontSize: 12 }} />
          </Box>
        )}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, minHeight: 28 }}>
          {loading ? (
            <Skeleton variant="rounded" width={64} height={26} />
          ) : (
            <Box component="span" sx={{ fontSize: 24, lineHeight: '28px', fontWeight: 600, letterSpacing: '-0.02em', color: C.text }}>{value}</Box>
          )}
          {!loading && aside}
        </Box>
        {loading ? (
          <Skeleton variant="rounded" width={110} height={12} />
        ) : (
          <Box component="span" sx={{ fontSize: 12, color: captionColor || C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {caption}
          </Box>
        )}
      </Box>
    </Box>
  )
}

/**
 * Five numbers a dispatcher checks first. Each card filters the board; click
 * again to clear. Avg/Delivered today come from the recent-orders slice.
 */
/** "…" for the orders placed in the last day, plus how many are older than that. */
function split(total, fresh, freshText) {
  const old = total - fresh
  if (old === 0) return freshText
  if (fresh === 0) return old === 1 ? 'Over a day old' : `All ${old} over a day old`
  return `${freshText} · ${old} stale`
}

export default function KpiStrip({ kpis, active, onSelect, loading, recentLoading }) {
  const toggle = k => onSelect(active === k ? null : k)
  const avg = kpis.avgToday
  const delta = avg != null && kpis.avgYesterday != null ? Math.round(avg - kpis.avgYesterday) : null
  const todayLoading = loading || recentLoading

  return (
    <Box
      role="group"
      aria-label="Today at a glance"
      sx={{
        display: { xs: 'flex', lg: 'grid' }, gridTemplateColumns: { lg: 'repeat(5, minmax(0, 1fr))' }, gap: 1.5,
        overflowX: { xs: 'auto', lg: 'visible' }, scrollSnapType: 'x mandatory', mx: { xs: -2, sm: 0 }, px: { xs: 2, sm: 0 }, pb: 0.25,
        // Snap to the padded edge, not the screen edge.
        scrollPaddingInline: { xs: '16px', sm: 0 },
        scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      <KpiCard
        id="needs_driver"
        active={active}
        onClick={toggle}
        loading={loading}
        icon={<PersonSearchOutlinedIcon />}
        tone={kpis.needsDriver > 0 ? 'warn' : 'neutral'}
        label="Needs driver"
        value={kpis.needsDriver}
        caption={
          kpis.needsDriver === 0
            ? 'Every delivery is covered'
            : split(kpis.needsDriver, kpis.needsDriverFresh, kpis.waitingOver15 > 0 ? `${kpis.waitingOver15} waiting over 15 min` : `${kpis.needsDriverFresh} under 15 min`)
        }
        captionColor={kpis.needsDriver > 0 ? AGE.warn.ink : undefined}
      />
      <KpiCard
        id="late"
        active={active}
        onClick={toggle}
        loading={loading}
        icon={<AccessTimeIcon />}
        tone={kpis.late > 0 ? 'late' : 'neutral'}
        label="Late"
        hint="Past the promised dropoff (or ready) time, however old"
        value={kpis.late}
        caption={kpis.late > 0 ? split(kpis.late, kpis.lateFresh, `Worst is ${span(kpis.worstLateMs)} past due`) : 'Nothing past due'}
        captionColor={kpis.late > 0 ? AGE.late.ink : undefined}
      />
      <KpiCard
        id="in_transit"
        active={active}
        onClick={toggle}
        loading={loading}
        icon={<LocalShippingOutlinedIcon />}
        tone="transit"
        label="In transit"
        value={kpis.inTransit}
        caption={kpis.driversOnRoad === 1 ? '1 driver on the road' : `${kpis.driversOnRoad} drivers on the road`}
      />
      <KpiCard
        id="avg_delivery"
        active={active}
        onClick={toggle}
        loading={todayLoading}
        icon={<TimerOutlinedIcon />}
        tone="neutral"
        label="Avg. delivery today"
        hint="Placed to delivered. Click to see today's slower-than-average orders."
        value={avg != null ? `${Math.round(avg)} min` : '—'}
        caption={
          avg == null ? 'No deliveries yet today'
            : delta == null || delta === 0 ? 'Same as yesterday'
              : (
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: delta < 0 ? TONE.delivered.fg : AGE.late.ink }}>
                  {delta < 0 ? <TrendingDownIcon sx={{ fontSize: 15 }} /> : <TrendingUpIcon sx={{ fontSize: 15 }} />}
                  {Math.abs(delta)} min {delta < 0 ? 'faster' : 'slower'} than yesterday
                </Box>
              )
        }
        aside={<Sparkline points={kpis.recentTrips} />}
      />
      <KpiCard
        id="delivered_today"
        active={active}
        onClick={toggle}
        loading={todayLoading}
        icon={<Inventory2OutlinedIcon />}
        tone="good"
        label="Delivered today"
        value={kpis.deliveredToday}
        caption={`${kpis.placedToday} placed today`}
      />
    </Box>
  )
}
